// src/pages/Dashboard/Admin/HeuresEnseignement/utils/teachingHoursUtils.js

export function toNumber(value) {
  const number = Number.parseFloat(value || 0);
  return Number.isFinite(number) ? number : 0;
}

export function formatHours(value, withSuffix = false) {
  const number = toNumber(value);

  const formatted = number
    .toFixed(2)
    .replace(/\.00$/, "")
    .replace(/(\.\d)0$/, "$1");

  return withSuffix ? `${formatted} h` : formatted;
}

export function formatMonthFR(month) {
  if (!month || !/^\d{4}-\d{2}$/.test(month)) return month || "—";

  const [year, rawMonth] = month.split("-").map(Number);
  const date = new Date(year, rawMonth - 1, 1);

  const label = date.toLocaleDateString("fr-FR", {
    month: "long",
    year: "numeric",
  });

  return label.charAt(0).toUpperCase() + label.slice(1);
}

export function sortTeachers(rows = []) {
  return [...rows].sort((a, b) =>
    `${a?.nom || ""} ${a?.prenom || ""}`.localeCompare(
      `${b?.nom || ""} ${b?.prenom || ""}`,
      "fr"
    )
  );
}

export function sortTeachingHours(rows = []) {
  return [...rows].sort((a, b) => {
    const monthCompare = String(a?.mois || "").localeCompare(
      String(b?.mois || "")
    );

    if (monthCompare !== 0) return monthCompare;

    return String(a?.semaine || "").localeCompare(String(b?.semaine || ""));
  });
}

export function getTeacherName(teachers = [], teacherId) {
  const teacher = teachers.find((item) => String(item.id) === String(teacherId));

  if (!teacher) return "Enseignant inconnu";

  return [teacher.nom, teacher.prenom].filter(Boolean).join(" ");
}

export function filterTeachers(teachers = [], filters = {}) {
  const query = String(filters.query || "").trim().toLowerCase();
  const teacherId = String(filters.teacherId || "").trim();

  return sortTeachers(teachers).filter((teacher) => {
    const fullName = `${teacher?.nom || ""} ${teacher?.prenom || ""}`.toLowerCase();

    const matchQuery = !query || fullName.includes(query);
    const matchTeacher = !teacherId || String(teacher.id) === teacherId;

    return matchQuery && matchTeacher;
  });
}

export function filterTeachingHours(hours = [], visibleTeachers = [], filters = {}) {
  const teacherIds = new Set(visibleTeachers.map((teacher) => String(teacher.id)));
  const month = String(filters.month || "").trim();

  return sortTeachingHours(hours).filter((hour) => {
    const matchTeacher = teacherIds.has(String(hour?.adherent_id));
    const matchMonth = !month || String(hour?.mois) === month;

    return matchTeacher && matchMonth;
  });
}

export function getUniqueMonths(hours = []) {
  return Array.from(
    new Set(hours.map((hour) => hour?.mois).filter(Boolean))
  ).sort();
}

export function getUniqueWeeksForMonth(hours = [], month) {
  return Array.from(
    new Set(
      hours
        .filter((hour) => String(hour?.mois) === String(month))
        .map((hour) => hour?.semaine)
        .filter(Boolean)
    )
  ).sort();
}

export function getMonthSections(hours = [], selectedMonth = "") {
  const months = selectedMonth ? [selectedMonth] : getUniqueMonths(hours);

  return months
    .map((month) => ({
      month,
      label: formatMonthFR(month),
      weeks: getUniqueWeeksForMonth(hours, month),
    }))
    .filter((section) => section.weeks.length > 0);
}

export function buildHoursIndex(hours = []) {
  const map = new Map();

  for (const hour of hours) {
    const key = `${hour?.mois}__${hour?.semaine}__${hour?.adherent_id}`;
    map.set(key, hour);
  }

  return map;
}

export function calculateTotalsByTeacher(hours = []) {
  const totals = {};

  for (const hour of hours) {
    const teacherId = String(hour?.adherent_id || "");
    totals[teacherId] = (totals[teacherId] || 0) + toNumber(hour?.heures);
  }

  return totals;
}

export function calculateMonthlyTotalsByTeacher(hours = []) {
  const totals = {};

  for (const hour of hours) {
    const key = `${hour?.mois}__${hour?.adherent_id}`;
    totals[key] = (totals[key] || 0) + toNumber(hour?.heures);
  }

  return totals;
}

export function calculateTeachingHoursStats({
  teachers = [],
  visibleTeachers = [],
  hours = [],
  visibleHours = [],
  totalsByTeacher = {},
}) {
  const totalHours = visibleHours.reduce(
    (sum, item) => sum + toNumber(item?.heures),
    0
  );

  const totalAllHours = hours.reduce((sum, item) => sum + toNumber(item?.heures), 0);

  const activeTeacherIds = new Set(
    visibleHours.map((hour) => String(hour?.adherent_id)).filter(Boolean)
  );

  const bestTeacher = visibleTeachers
    .map((teacher) => ({
      teacher,
      total: totalsByTeacher[String(teacher.id)] || 0,
    }))
    .sort((a, b) => b.total - a.total)[0];

  const averageByTeacher =
    visibleTeachers.length > 0 ? totalHours / visibleTeachers.length : 0;

  return {
    teachersCount: teachers.length,
    visibleTeachersCount: visibleTeachers.length,
    entriesCount: visibleHours.length,
    allEntriesCount: hours.length,
    activeTeachersCount: activeTeacherIds.size,
    totalHours,
    totalAllHours,
    averageByTeacher,
    bestTeacherName: bestTeacher?.teacher
      ? `${bestTeacher.teacher.nom || ""} ${bestTeacher.teacher.prenom || ""}`.trim()
      : "—",
    bestTeacherHours: bestTeacher?.total || 0,
  };
}

/** Helpers ISO week */
export function getISOWeekYear(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  d.setUTCDate(d.getUTCDate() + 4 - (d.getUTCDay() || 7));
  return d.getUTCFullYear();
}

export function getISOWeekNumber(date) {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = d.getUTCDay() || 7;

  d.setUTCDate(d.getUTCDate() + 4 - dayNum);

  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));

  return Math.ceil((((d - yearStart) / 86400000) + 1) / 7);
}

export function toYYYYMM(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

export function derivePeriodFromDate(dateString) {
  if (!dateString) return { semaine: "", mois: "" };

  const date = new Date(`${dateString}T12:00:00`);
  const isoYear = getISOWeekYear(date);
  const week = String(getISOWeekNumber(date)).padStart(2, "0");

  return {
    semaine: `${isoYear}-W${week}`,
    mois: toYYYYMM(date),
  };
}

export function hasDuplicateTeachingHours(hours = [], params = {}) {
  const semaine = String(params.semaine || "");
  const teacherId = String(params.teacherId || "");
  const ignoreId = params.ignoreId ? String(params.ignoreId) : "";

  if (!semaine || !teacherId) return false;

  return hours.some((hour) => {
    if (ignoreId && String(hour?.id) === ignoreId) return false;

    return (
      String(hour?.semaine) === semaine &&
      String(hour?.adherent_id) === teacherId
    );
  });
}